import type Graph from 'graphology';
import { GitMerge, Workflow } from 'lucide-react';
import { useMemo } from 'react';

interface ProcessRow {
  readonly id: string;
  readonly label: string;
  readonly stepCount: number;
}

export interface ProcessesListProps {
  readonly graph: Graph | null;
  readonly onNodeSelect: (nodeId: string) => void;
  readonly onViewDiagram: (processNodeId: string) => void;
}

export function ProcessesList({
  graph,
  onNodeSelect,
  onViewDiagram,
}: ProcessesListProps): JSX.Element {
  const processes = useMemo<ProcessRow[]>(() => {
    if (!graph) return [];
    const out: ProcessRow[] = [];
    graph.forEachNode((id, attrs) => {
      if (attrs.kind !== 'Process') return;
      let stepCount = 0;
      graph.forEachInEdge(id, (_key, edgeAttrs) => {
        if (edgeAttrs.relationType === 'BELONGS_TO_PROCESS') stepCount += 1;
      });
      const label = (attrs.label as string) ?? id;
      out.push({ id, label, stepCount });
    });
    out.sort((a, b) => b.stepCount - a.stepCount);
    return out;
  }, [graph]);

  if (!graph) return <Empty message="Graph not loaded yet." />;
  if (processes.length === 0) return <Empty message="No processes detected." />;

  return (
    <div className="scrollbar-thin flex-1 overflow-y-auto p-2">
      <ul className="flex flex-col gap-1.5">
        {processes.map((p) => (
          <li
            key={p.id}
            className="group flex items-center gap-2 rounded-lg border border-border-subtle bg-surface/60 p-2 transition-all hover:border-accent/30 hover:bg-surface"
          >
            <Workflow className="h-3.5 w-3.5 flex-none text-node-process" />
            <button
              type="button"
              onClick={() => onNodeSelect(p.id)}
              title={p.label}
              className="flex min-w-0 flex-1 cursor-pointer flex-col items-start text-left"
            >
              <span className="w-full truncate font-mono text-xs text-text-primary group-hover:text-text-primary">
                {p.label}
              </span>
              <span className="text-[10px] text-text-muted">{p.stepCount} steps</span>
            </button>
            <button
              type="button"
              onClick={() => onViewDiagram(p.id)}
              className="inline-flex items-center gap-1 rounded-md border border-cyan-400/30 bg-cyan-400/10 px-2 py-1 text-[10px] font-medium text-cyan-300 transition-colors hover:border-cyan-400/60 hover:bg-cyan-400/20"
            >
              <GitMerge className="h-3 w-3" />
              Diagram
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Empty({ message }: { readonly message: string }): JSX.Element {
  return (
    <div className="flex h-full items-center justify-center px-4 py-6 text-center text-xs text-text-muted">
      {message}
    </div>
  );
}
